import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { Delete, Publish, CheckCircle } from "@mui/icons-material";
import { publishChapter } from "../utils/api";
import {
  publishInteractiveChapter,
  validateInteractiveChapter,
} from "../utils/apis/interactiveStoriesAPI";
import { t } from "@lingui/macro";

const ChapterList = ({ storyID, chapters = {}, onDelete, onChapterPublished, type = "text" }) => {
  const navigate = useNavigate();
  const [loadingPublishIndex, setLoadingPublishIndex] = useState(null);
  const [successPublishIndex, setSuccessPublishIndex] = useState(null);
  const [loadingDeleteIndex, setLoadingDeleteIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hoveringBlockedPublish, setHoveringBlockedPublish] = useState(false);
  const [hoveringBlockedByPrevious, setHoveringBlockedByPrevious] = useState(null);
  const [loadingPublish, setLoadingPublish] = useState({});
  const [successPublish, setSuccessPublish] = useState({});

  // ────────────────────────────── Firestore + validation helpers ──────────────────────────────
  const getMillis = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    if (typeof ts._seconds === "number") return ts._seconds * 1000;
    if (typeof ts === "number") return ts;
    return null;
  };

  const getInteractiveValidationInfo = (chapter) => {
    if (!chapter || type !== "interactive") return null;

    const v = chapter.validation;
    if (!v || !v.lastRunAt) {
      return { state: "not_run" };
    }

    const updatedAtMillis = getMillis(chapter.updatedAt || chapter.createdAt);
    const atRunMillis = getMillis(v.chapterUpdatedAtAtRun);

    const isStale =
      updatedAtMillis && atRunMillis && updatedAtMillis > atRunMillis;

    if (isStale) {
      return { state: "stale" };
    }

    return v.readyToPublishAtRun
      ? { state: "ok" }
      : { state: "failed" };
  };

  const isInteractiveValidationOk = (chapter) => {
    if (type !== "interactive") return true;

    const info = getInteractiveValidationInfo(chapter);

    // We only want to BLOCK when we *know* validation has FAILED.
    // - not_run  → allow click (we'll validate on publish)
    // - stale    → allow click (we'll re-validate on publish)
    // - ok       → allow click (already good)
    // - failed   → BLOCK (critical errors)
    if (!info) return true;

    return info.state !== "failed";
  };

  const chaptersArray = Array.isArray(chapters)
    ? chapters.map((chapter, index) => ({ ...chapter, index }))
    : Object.entries(chapters || {}).map(([index, chapter]) => ({ ...chapter, index }));

  const anyDelisted = chaptersArray.some((chap) => chap.delisted === true);

  const handlePublishDisabledReason = (charCount, status, chapterIndex, chaptersArray = []) => {
    const currentChapter = chaptersArray.find(
      (c) => parseInt(c.index) === parseInt(chapterIndex)
    );

    if (!currentChapter) {
      return t`Chapter data is missing.`;
    }

    // Interactive stories: only block when we *know* validation has FAILED.
    if (type === "interactive") {
      const vInfo = getInteractiveValidationInfo(currentChapter);

      // ❌ Only FAILED validation gives a disable reason.
      if (vInfo?.state === "failed") {
        return t`Validation failed. Fix chapter errors.`;
      }

      // ✅ not_run / stale / ok → no disable reason here.
    }


    if (status !== "SAVED" && currentChapter.delisted) {
      return t`This chapter was delisted, edit it and publish again.`;
    }

    // Skip char count validation for interactive stories
    if (type === "text") {
      if (charCount < 5000) {
        return t`Chapter is too short (${charCount}/5000).`;
      }
      if (charCount > 60000) {
        return t`Chapter is too long (${charCount}/60000).`;
      }
    }

    const hasBlockingDelisted = chaptersArray.some(
      (c) => c.delisted && parseInt(c.index) < parseInt(chapterIndex)
    );

    if (hasBlockingDelisted) {
      return t`A previous chapter is delisted. Resolve it first.`;
    }

    const prevIndex = parseInt(chapterIndex) - 1;
    const prevChapter = chaptersArray.find((c) => parseInt(c.index) === prevIndex);

    if (prevIndex >= 1 && (!prevChapter || !prevChapter.published)) {
      return t`You must publish Chapter ${prevIndex} first.`;
    }

    if (currentChapter.status === "SAVED" && !currentChapter.delisted) {
      const allOthersNotDelisted = chaptersArray.every(
        (c) => parseInt(c.index) !== chapterIndex ? !c.delisted : true
      );
      if (!allOthersNotDelisted) {
        return t`Another chapter is delisted. Resolve it first.`;
      }
    }

    return null;
  };


  const hasBlockingDelistedChapter = chaptersArray.some(
    (c) => c.delisted && parseInt(c.index) < parseInt(c.index)
  );

  const handlePublishChapter = async (chapterIndex) => {
    const isInteractive = type === "interactive";

    // Always resolve the chapter from chaptersArray (fresh props)
    const currentChapter = chaptersArray.find(
      (c) => String(c.index) === String(chapterIndex)
    );

    if (!currentChapter) {
      console.error("Publish: chapter not found for index", chapterIndex);
      setSnackbarMessage(t`Chapter data is missing.`);
      setSnackbarOpen(true);
      return;
    }

    // For INTERACTIVE validation we need the Firestore document ID.
    // Your payload example shows `chapterID`, but we’re defensive:
    const validationChapterId =
      isInteractive &&
      (
        currentChapter.chapterID ||  // preferred (your example)
        currentChapter.chapterId ||
        currentChapter.id ||
        currentChapter.firestoreId ||
        null
      );

    setLoadingPublish((prev) => ({ ...prev, [chapterIndex]: true }));

    try {
      // ───────────────────────── INTERACTIVE: validate by ID first ─────────────────────────
      if (isInteractive) {
        if (!validationChapterId) {
          console.error("No chapterID found for interactive validation", {
            chapterIndex,
            currentChapter,
          });
          setSnackbarMessage(
            t`This interactive chapter is missing its ID. Please open and save it once, then try again.`
          );
          setSnackbarOpen(true);
          return;
        }

        let validation;
        try {
          // ✅ validateInteractiveChapter expects { storyID, chapterID }
          validation = await validateInteractiveChapter(
            storyID,
            validationChapterId
          );
        } catch (err) {
          console.error("validateInteractiveChapter failed:", err);
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            t`Validation request failed. Please try again.`;
          setSnackbarMessage(
            t`Validation failed before publishing: ${msg}`
          );
          setSnackbarOpen(true);
          return;
        }

        // If backend says it's NOT ready, do NOT publish – just show the message
        // and ask parent to refresh the story/chapters (like a mini reload).
        if (!validation?.readyToPublish) {
          const msg =
            validation?.message ||
            t`Validation found issues. Open the chapter preview to see and fix them.`;

          setSnackbarMessage(msg);
          setSnackbarOpen(true);

          // 🔁 Let parent refetch from backend so validation + button states update
          onChapterPublished?.("VALIDATION_FAILED");

          return;
        }
      }

      // ───────────────────────── PUBLISH ─────────────────────────
      // TEXT:      backend expects the numeric index → chapterIndex
      // INTERACTIVE: backend also expects the index, NOT the Firestore ID.
      const fn = isInteractive ? publishInteractiveChapter : publishChapter;
      const response = await fn(storyID, chapterIndex);

      // For TEXT we still rely on `{ success: true }`.
      // For INTERACTIVE we treat "no error thrown" as success because
      // the Cloud Function doesn't currently return `{ success: true }`.
      const publishOk = isInteractive || response?.success;

      if (!publishOk) {
        throw new Error("Publishing chapter failed");
      }

      setSuccessPublish((prev) => ({ ...prev, [chapterIndex]: true }));
      setSnackbarMessage(t`Chapter published successfully!`);
      setSnackbarOpen(true);

      console.log("[ChapterList] notifying parent onChapterPublished", {
        type,
        chapterIndex,
        response,
      });

      // ✅ Let parent refresh chapters for both text + interactive.
      onChapterPublished?.(chapterIndex);

      setTimeout(() => {
        setSuccessPublish((prev) => ({ ...prev, [chapterIndex]: false }));
      }, 2000);

    } catch (error) {
      console.error(t`Error publishing chapter ${chapterIndex}:`, error);
      setSnackbarMessage(
        t`Failed to publish chapter ${chapterIndex}: ${error}`
      );
      setSnackbarOpen(true);
      if (String(error).includes(t`story must have a cover image`)) {
        onChapterPublished?.("MISSING_COVER");
      }
    } finally {
      setLoadingPublish((prev) => ({ ...prev, [chapterIndex]: false }));
    }
  };



  const handleDeleteConfirm = (chapterIndex) => {
    setChapterToDelete(chapterIndex);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteChapter = async () => {
    if (chapterToDelete !== null) {
      setDeleteConfirmOpen(false);
      setLoadingDeleteIndex(chapterToDelete);
      try {
        await onDelete(chapterToDelete);
        setSnackbarMessage(t`Chapter ${chapterToDelete} deleted successfully!`);
        setSnackbarOpen(true);
      } catch (error) {
        console.error(`Error deleting chapter ${chapterToDelete}:`, error);
        setSnackbarMessage(t`Failed to delete chapter ${chapterToDelete}.`);
        setSnackbarOpen(true);
      } finally {
        setLoadingDeleteIndex(null);
      }
    }
  };

  const getStatusColor = (status, isHighlighted) => {
    if (isHighlighted) return "red";
    switch (status) {
      case "PUBLISHED":
        return "green";
      case "SAVED":
        return "gold";
      case "DELISTED":
        return "red";
      default:
        return "gray";
    }
  };

  const canPublishChapter = (chapterIndex, chaptersArray) => {
    const chapterIdx = parseInt(chapterIndex);

    if (chapterIdx > 1) {
      const prevChapter = chaptersArray.find(
        (c) => parseInt(c.index) === chapterIdx - 1
      );
      if (!prevChapter || !prevChapter.published) {
        return false;
      }
    }
    const currentChapter = chaptersArray.find(
      (c) => parseInt(c.index) === chapterIdx
    );

    if (!currentChapter) return false;

    if (currentChapter.status === "DELISTED") {
      return false;
    }

    if (currentChapter.delisted && currentChapter.status === "SAVED") {
      const hasEarlierDelisted = chaptersArray.some(
        (c) => c.delisted && parseInt(c.index) < chapterIdx
      );
      return !hasEarlierDelisted;
    }

    if (currentChapter.status === "SAVED" && currentChapter.delisted) {
      const allPreviousPublished = chaptersArray.every((c) =>
        parseInt(c.index) < chapterIdx ? c.published : true
      );
      return allPreviousPublished;
    }

    if (currentChapter.status === "SAVED" && !currentChapter.delisted) {
      const allOthersNotDelisted = chaptersArray.every(
        (c) => parseInt(c.index) !== chapterIdx ? !c.delisted : true
      );
      return allOthersNotDelisted;
    }

    console.warn("Unhandled case for chapter", chapterIdx, currentChapter);
    return false;
  };

  const statusLabels = {
    PUBLISHED: t`PUBLISHED`,
    SAVED: t`DRAFT`,
    DELISTED: t`DELISTED`,
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t`Chapters`}
      </Typography>

      <List>
        {chaptersArray.map((chapter, idx) => {
          const validationInfo =
            type === "interactive" ? getInteractiveValidationInfo(chapter) : null;

          const displayLabel =
            chapter.status === "PUBLISHED" && anyDelisted
              ? t`READY TO PUBLISH`
              : statusLabels[chapter.status] || t`Unknown`;

          return (
            <React.Fragment key={chapter.index}>
              <ListItem
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 1.5, sm: 0 },
                  transition: "box-shadow 0.3s ease-in-out",
                  boxShadow:
                    hoveringBlockedPublish && chapter.delisted
                      ? "0px 0px 12px 3px rgba(255, 0, 0, 0.8)"
                      : hoveringBlockedByPrevious === chapter.index
                        ? "0px 0px 12px 3px rgba(255, 215, 0, 0.8)"
                        : "none",
                  "&:hover": {
                    boxShadow:
                      hoveringBlockedPublish && chapter.delisted
                        ? "0px 0px 12px 3px rgba(255, 0, 0, 1)"
                        : hoveringBlockedByPrevious === chapter.index
                          ? "0px 0px 12px 3px rgba(255, 215, 0, 1)"
                          : "none",
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() =>
                        navigate(`/mystories/${storyID}/${chapter.index}`, {
                          state: {
                            type,
                            contentUrl: chapter.contentUrl,
                            title: chapter.title,
                            updatedAt: chapter.updatedAt
                              ? typeof chapter.updatedAt.toDate === "function"
                                ? chapter.updatedAt.toDate().toISOString()
                                : new Date(
                                  chapter.updatedAt._seconds * 1000
                                ).toISOString()
                              : null,
                          },
                        })
                      }
                    >
                      {chapter.title || t`Chapter ${chapter.index}`}
                    </Typography>
                  }
                  secondary={
                    <>
                      {chapter.status === "SAVED" && chapter.delisted ? (
                        <Tooltip
                          title={t`Your chapter was delisted and you have a new draft. Publish it when you are ready`}
                          placement="top-start"
                          enterTouchDelay={0}
                          slotProps={{
                            tooltip: {
                              sx: { fontSize: "1rem" },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background:
                                  "conic-gradient(red 0deg, red 180deg, gold 180deg, gold 360deg)",
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {displayLabel}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : chapter.status === "SAVED" && chapter.published ? (
                        <Tooltip
                          title={t`Your chapter is published but you have a new draft`}
                          placement="top-start"
                          enterTouchDelay={0}
                          slotProps={{
                            tooltip: {
                              sx: { fontSize: "1rem" },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background:
                                  "conic-gradient(gold 0deg, gold 180deg, green 180deg, green 360deg)",
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {statusLabels[chapter.status] || t`Unknown`}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: getStatusColor(chapter.status),
                            }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {displayLabel}
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="caption" color="textSecondary">
                        {t`Updated at:`}{" "}
                        {chapter.updatedAt
                          ? new Date(
                            chapter.updatedAt._seconds * 1000
                          ).toLocaleString()
                          : t`Unknown`}
                      </Typography>

                      {type === "interactive" && validationInfo?.state === "failed" && (
                        <Typography variant="caption" color="textSecondary">
                          {t` Validation failed. Fix chapter errors.`}
                        </Typography>
                      )}

                    </>
                  }
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", sm: "row" },
                    gap: 1,
                    mt: { xs: 1, sm: 0 },
                    width: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                  }}
                >
                  {parseInt(chapter.index) === chaptersArray.length && (
                    <IconButton
                      sx={{ color: "error.main" }}
                      onClick={() => handleDeleteConfirm(chapter.index)}
                      disabled={loadingDeleteIndex === chapter.index}
                    >
                      {loadingDeleteIndex === chapter.index ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Delete />
                      )}
                    </IconButton>
                  )}

                  {chapter.status !== "PUBLISHED" && (
                    <Tooltip
                      title={
                        handlePublishDisabledReason(
                          chapter.charCount,
                          chapter.status,
                          chapter.index,
                          chaptersArray
                        ) || ""
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={2000}
                      onOpen={() => {
                        setHoveringBlockedPublish(true);
                        const prevIndex = parseInt(chapter.index) - 1;
                        const prevChapter = chaptersArray.find(
                          (c) => parseInt(c.index) === prevIndex
                        );
                        if (prevChapter && !prevChapter.published) {
                          setHoveringBlockedByPrevious(prevChapter.index);
                        }
                      }}
                      onClose={() => {
                        setHoveringBlockedPublish(false);
                        setHoveringBlockedByPrevious(null);
                      }}
                      disableHoverListener={
                        !handlePublishDisabledReason(
                          chapter.charCount,
                          chapter.status,
                          chapter.index,
                          chaptersArray
                        )
                      }
                    >
                      <span style={{ width: "100%" }}>
                        <Button
                          fullWidth={window.innerWidth < 600}
                          variant="contained"
                          color={
                            successPublish[chapter.index] ? "success" : "primary"
                          }
                          startIcon={
                            successPublish[chapter.index] ? (
                              <CheckCircle />
                            ) : (
                              <Publish />
                            )
                          }
                          onClick={() => handlePublishChapter(chapter.index)}
                          disabled={
                            loadingPublish[chapter.index] ||
                            successPublish[chapter.index] ||
                            (type === "text" &&
                              (chapter.charCount < 5000 ||
                                chapter.charCount > 60000)) ||
                            chapter.status === "DELISTED" ||
                            (chapter.status !== "SAVED" && chapter.delisted) ||
                            chaptersArray.some(
                              (c) =>
                                c.delisted &&
                                c.status !== "SAVED" &&
                                parseInt(c.index) < parseInt(chapter.index)
                            ) ||
                            !canPublishChapter(chapter.index, chaptersArray) ||
                            (type === "interactive" &&
                              !isInteractiveValidationOk(chapter))
                          }
                          sx={{
                            minWidth: 130,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minHeight: 24,
                            }}
                          >
                            <CircularProgress
                              size={20}
                              color="inherit"
                              sx={{
                                mr: 1,
                                visibility: loadingPublish[chapter.index]
                                  ? "visible"
                                  : "hidden",
                                position: "absolute",
                              }}
                            />
                            <Typography
                              sx={{
                                visibility: loadingPublish[chapter.index]
                                  ? "hidden"
                                  : "visible",
                              }}
                            >
                              {successPublish[chapter.index]
                                ? t`Published!`
                                : t`Publish`}
                            </Typography>
                          </Box>
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </ListItem>

              {idx < chaptersArray.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t`Confirm Deletion`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t`Are you sure you want to delete Chapter ${chapterToDelete}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="secondary">
            {t`Cancel`}
          </Button>
          <Button onClick={handleDeleteChapter} color="error" autoFocus>
            {t`Delete`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ChapterList;
